import React from 'react';

import { IconCircleCheckFilled } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { CustomQuizQuestion, QuizDetail } from '@cfa/pathway-fe-shared/xp';
import {
  Card,
  CardContent,
  Icon,
  Typography,
  useBreakpoints,
  useMediaQuery,
} from '@cfa/react-components';
import { Radio, RadioGroup } from '@cfa/react-core';

import { cn } from '@/utils/tailwind';

import GenericError from 'components/Error/GenericError';
import { getNameFromLanguage } from 'utils/language';

interface QuizPreviewCardProps {
  quiz: QuizDetail | undefined;
  quizError: unknown;
}

const QuizPreviewCard: React.FC<QuizPreviewCardProps> = ({
  quiz,
  quizError,
}) => {
  const { t } = useTranslation();
  const breakpoints = useBreakpoints();
  const isSmAndUp = useMediaQuery(breakpoints.up('sm'));

  if (quizError) {
    return <GenericError />;
  }

  if (!quiz?.questions.length) {
    return (
      <Card elevation={2}>
        <CardContent>
          <Typography className="text-feedback-info-base px-0 py-4 text-center">
            {t('TrainingPlans.previewQuiz.noQuestions')}
          </Typography>
          <Typography className="text-feedback-info-base text-center">
            {t('TrainingPlans.previewQuiz.noQuestionsHelpText')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return quiz.questions.map(
    (questionData: CustomQuizQuestion, questionIndex: number) => (
      <Card
        className={cn({
          'mt-[18px]': questionIndex > 0,
        })}
        elevation={2}
        key={questionIndex}
      >
        <CardContent>
          <Typography className="text-brand-dark-gray dark:text-contrast! px-0 py-4 text-left font-bold!">
            {getNameFromLanguage(questionData.question)}
          </Typography>
          {questionData.answers.map((answerData, answerIndex) => (
            <div
              className="flex-row items-baseline justify-center"
              key={answerIndex}
            >
              <RadioGroup orientation="vertical" className="w-full!">
                <div
                  className={cn(
                    'br-1 flex flex-wrap justify-between border border-solid border-transparent p-2',
                    {
                      'bg-feedback-success-container! border-feedback-success-base!':
                        answerData.correct,
                    },
                  )}
                >
                  <Radio
                    className={cn({
                      '[&::before]:border-feedback-success-base! [&::before]:border-[6px]!':
                        answerData.correct,
                    })}
                    value={String(answerData.correct)}
                    isDisabled={!answerData.correct}
                  >
                    {getNameFromLanguage(answerData.answer)}
                  </Radio>
                  {answerData.correct && (
                    <Typography
                      className={cn(
                        'text-feedback-success-base! m-1 flex items-center gap-2 pl-9 font-medium!',
                        { 'pl-0': isSmAndUp },
                      )}
                    >
                      {t('TrainingPlans.previewQuiz.correctAnswer')}
                      <Icon className="size-5!" icon={IconCircleCheckFilled} />
                    </Typography>
                  )}
                </div>
              </RadioGroup>
            </div>
          ))}
        </CardContent>
      </Card>
    ),
  );
};

export default QuizPreviewCard;
